// The hand: what the cards are (PLAN-UI §28.3, U7).
//
// The model half of the client's `hand.ts`, which keeps the fan — the spread,
// the swing, the lift and the seats are arithmetic about pixels and stay with
// the thing that draws them. What a card *is* is read off the run.

import type { CardIcon, CardRarity, Rarity } from "src/api/chrome";
import { CARD_TABLE, type CardId } from "src/engine/rules/campaign/cards";
import type { GameState } from "src/engine/rules/commands";

import { RARITY_NAME, rarityOfCard } from "./rarity";

// --- the cards --------------------------------------------------------------

/**
 * The picture in the art window.
 *
 * A tagged union rather than `IconName | string`, because an `IconName` **is**
 * a string: a bare union could not be told apart at the call site without a
 * lookup into the registry, which is exactly the kit-internal thing a call
 * site is not supposed to know about.
 */
export type CardGlyph = { kind: "icon"; name: CardIcon } | { kind: "text"; text: string };

export interface HandCard {
  /** The deck instance — what `play-card` is dispatched with. */
  instance: number;
  id: CardId;
  name: string;
  /** The terse line on the card's own face. `Card.short`. */
  short: string;
  /** The fuller account, on the card's hover card. `Card.text`. */
  text: string;
  /** It wants a square before it can be played. */
  targeted: boolean;
  /** **The kit's five, not `rarity.ts`'s** — see `kitRarity`. */
  rarity: CardRarity;
  /**
   * What to call that rarity, resolved here.
   *
   * `RARITY_NAME` is keyed by `rarity.ts`'s ladder and the card carries the
   * kit's, so the alternative is a second name table in the component — the
   * drift this codebase names about every count standing in for a value.
   */
  rarityName: string;
  /** This is the card being aimed: the board is waiting for a square. */
  picking: boolean;
  glyph: CardGlyph;
}

export interface HandModel {
  cards: HandCard[];
}

/** A card a trophy offers (§31 A2), faced like a held one. */
export interface TrophyCardModel {
  id: CardId;
  name: string;
  short: string;
  text: string;
  rarity: CardRarity;
  rarityName: string;
  glyph: CardGlyph;
}

export interface TrophyModel {
  id: number;
  offer: TrophyCardModel[];
}

/** The run's unclaimed trophies, oldest first; empty outside a run. */
export function trophyModel(state: GameState): TrophyModel[] {
  return (state.run?.trophies ?? []).map((trophy) => ({
    id: trophy.id,
    offer: trophy.offer.flatMap((id) => {
      const card = CARD_TABLE[id];
      if (!card) return [];
      const rarity = rarityOfCard(card.id);
      return [
        {
          id: card.id,
          name: card.name,
          short: card.short,
          text: card.text,
          rarity: kitRarity(rarity),
          rarityName: RARITY_NAME[rarity],
          glyph: cardGlyph(card.id)
        }
      ];
    })
  }));
}

/**
 * The kit's rarity word for `rarity.ts`'s.
 *
 * **Mapped, never renamed on either side.** `tokens.test.ts` pins
 * `TOKENS.rarity` to the kit sheet's `:root` block, where the top step is
 * `legend`; `rarity.ts` has said `legendary` since the loot ramp was written
 * and `RARITY_NAME`/`RARITY_COLOUR` are keyed by it. Renaming either end to
 * agree breaks the guard or the ladder, so the one word that differs is
 * translated in one function.
 */
export function kitRarity(rarity: Rarity): CardRarity {
  return rarity === "legendary" ? "legend" : rarity;
}

/**
 * The picture per card, where the icon set has none.
 *
 * **Text glyphs, and no new assets.** The kit's icons were sourced for
 * chrome — tools, structures, the pause button, the six ranks — and a card wants a picture of
 * an *idea*, which is a commission rather than a lookup. The mock draws these
 * as glyphs (`🛡 ▦ ⚙ 🎭 ❄`) and so does this; the art window is the thing that
 * makes them read as cards, and `✦` covers a card added before its glyph is.
 */
export const CARD_GLYPH: Partial<Record<CardId, string>> = {
  "reactor-boosters": "🚀",
  march: "🥁",
  "full-production-gambit": "⚙",
  "stasis-field": "❄",
  "micro-singularity-bomb": "◉",
  "programmer-oil": "🛢",
  bridges: "▦",
  "signet-crown": "♛",
  "hydraulic-clasp": "⛓",
  "iff-spoofer": "🎭",
  "time-fluxxer": "⏳",
  "yearning-for-the-mines": "⛏",
  coronation: "♔",
  "spatial-manifold": "◈",
  "the-enemy-is-at-the-gates": "⚔"
};

/** The cards the icon set actually draws. `Hotbar`'s `STRUCTURE_ICON` rule. */
const CARD_ICON: Partial<Record<CardId, CardIcon>> = {
  // The kit's `moat` was drawn for this card and has no other reader.
  "static-moat": "moat"
};

/** What goes in the art window: the icon where there is one, else the glyph. */
export function cardGlyph(id: CardId): CardGlyph {
  const icon = CARD_ICON[id];
  if (icon) return { kind: "icon", name: icon };
  return { kind: "text", text: CARD_GLYPH[id] ?? "✦" };
}

/**
 * The hand, in `run.hand` order.
 *
 * `null` outside a run: there is no deck, so there is no zone — the component
 * renders nothing and registers no inset rather than drawing an empty shelf.
 *
 * **A deck entry that does not resolve is skipped**, which is what `Resting`
 * did: `run.hand` holds instance ids and `run.deck` the instances, and a hand
 * naming an id the deck no longer holds is a bug we would rather draw one card
 * short than crash on.
 */
export function handModel(state: GameState, aiming: number | undefined): HandModel | null {
  const run = state.run;
  if (!run) return null;

  const cards: HandCard[] = [];
  for (const instance of run.hand) {
    const held = run.deck.find((entry) => entry.id === instance);
    if (!held) continue;
    const card = CARD_TABLE[held.card];
    if (!card) continue;
    // **How rare it is, derived from how often it drops** rather than declared
    // beside the card — a second table saying which cards are rare is a second
    // thing to keep in step with the loot weights.
    const rarity = rarityOfCard(card.id);
    cards.push({
      instance,
      id: card.id,
      name: card.name,
      short: card.short,
      text: card.text,
      targeted: card.targeted,
      rarity: kitRarity(rarity),
      rarityName: RARITY_NAME[rarity],
      picking: aiming === instance,
      glyph: cardGlyph(card.id)
    });
  }
  return { cards };
}
